import { useCallback, useEffect, useState } from 'react';

import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';
import { isExternalLink } from 'src/routes/utils';

import { navSectionClasses } from '../classes';
import { NavCollapse, NavLi, NavUl } from '../styles';
import { NavItem } from './nav-item';

import { useTranslate } from 'react-admin';
import type { NavListProps, NavSubListProps } from '../types';

// ----------------------------------------------------------------------

/**
 * Whether an item (or any of its nested children) matches the current pathname.
 */
export function isNavItemActive(item: any, pathname: string): boolean {
  const selfActive =
    !!item.path &&
    item.path !== '#' &&
    (pathname === item.path || pathname.startsWith(`${item.path}/`));

  return selfActive || (item.children ? item.children.some((c: any) => isNavItemActive(c, pathname)) : false);
}

/**
 * Index of the sibling that contains the active route (-> null when none).
 */
function getActiveIndex(items: any[], pathname: string): number | null {
  const index = items.findIndex((item) => isNavItemActive(item, pathname));
  return index === -1 ? null : index;
}

// ----------------------------------------------------------------------

export function NavList({
  data,
  render,
  depth,
  slotProps,
  enabledRootRedirect,
  showIcon = false,
  open = false,
  onToggle,
}: NavListProps) {
  const pathname = usePathname();
  const t = useTranslate();

  const hasActiveChild = data.children?.some((child: any) => isNavItemActive(child, pathname));

  const active =
    useActiveLink(data.path, !!data.children) ||
    pathname.startsWith(`${data.path}/`) ||
    hasActiveChild;

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      onToggle?.();
    }
  }, [data.children, onToggle]);

  const renderNavItem = (
    <NavItem
      render={render}
      path={data.path}
      icon={depth === 1 || showIcon ? data.icon : undefined}
      info={data.info}
      title={t(data.title)}
      caption={data.caption}
      tooltip={data.tooltip}
      depth={depth}
      active={active}
      disabled={data.disabled}
      hasChild={!!data.children}
      open={data.children && open}
      externalLink={isExternalLink(data.path)}
      enabledRootRedirect={enabledRootRedirect}
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      onClick={handleToggleMenu}
    />
  );

  if (data.roles && slotProps?.currentRole) {
    if (!data?.roles?.includes(slotProps?.currentRole)) {
      return null;
    }
  }

  if (data.children) {
    return (
      <NavLi
        disabled={data.disabled}
        sx={{
          [`& .${navSectionClasses.li}`]: {
            '&:first-of-type': { mt: 'var(--nav-item-gap)' },
          },
        }}
      >
        {renderNavItem}

        <NavCollapse data-group={data.title} in={open} depth={depth} unmountOnExit mountOnEnter>
          <NavSubList
            data={data.children}
            render={render}
            depth={depth}
            slotProps={slotProps}
            enabledRootRedirect={enabledRootRedirect}
            showIcon={data.childIcons}
          />
        </NavCollapse>
      </NavLi>
    );
  }

  return <NavLi disabled={data.disabled}>{renderNavItem}</NavLi>;
}

// ----------------------------------------------------------------------

function NavSubList({
  data,
  render,
  depth,
  slotProps,
  enabledRootRedirect,
  showIcon,
}: NavSubListProps) {
  const pathname = usePathname();

  // Accordion: only one sibling expanded at a time.
  // Recomputed every render so it re-syncs when `data` arrives (e.g. permissions
  // load async on reload) — not just when the pathname changes.
  const activeIndex = getActiveIndex(data, pathname);
  const [openIndex, setOpenIndex] = useState<number | null>(activeIndex);

  useEffect(() => {
    if (activeIndex !== null) {
      setOpenIndex(activeIndex);
    }
  }, [activeIndex]);

  const handleToggle = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  return (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {data.map((list, index) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
          showIcon={showIcon}
          open={openIndex === index}
          onToggle={() => handleToggle(index)}
        />
      ))}
    </NavUl>
  );
}
